// Dashboard Metrics API Types
// GET /api/v1/admin/metrics/all

// User Metrics
export interface UsersByCountry {
  country: string;
  count: number;
}

export interface UsersByCity {
  city: string;
  count: number;
}

export interface UserMetrics {
  activeUsers: number;
  inactiveUsers: number;
  emailVerificationRate: number;
  phoneVerificationRate: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  usersByCountry: UsersByCountry[];
  usersByCity: UsersByCity[];
}

// Exhibitor Metrics
export interface BoothTypeDistribution {
  boothType: string;
  count: number;
}

export interface ExhibitorsByCategory {
  categoryId: number;
  categoryName: string;
  count: number;
}

export interface ExhibitorMetrics {
  verifiedExhibitors: number;
  unverifiedExhibitors: number;
  featuredExhibitors: number;
  startupCompanies: number;
  establishedCompanies: number;
  boothTypeDistribution: BoothTypeDistribution[];
  exhibitorsByCategory: ExhibitorsByCategory[];
}

// Leads Metrics
export interface LeadsByTag {
  tag: string;
  count: number;
}

export interface TopExhibitorByLeads {
  exhibitorId: number;
  exhibitorName: string;
  leadCount: number;
}

export interface InteractionByType {
  type: string;
  count: number;
}

export interface InteractionByOutcome {
  outcome: string;
  count: number;
}

export interface LeadsMetrics {
  leadsByTag: LeadsByTag[];
  topExhibitorsByLeads: TopExhibitorByLeads[];
  interactionsByType: InteractionByType[];
  interactionsByOutcome: InteractionByOutcome[];
}

// Meetings Metrics
export interface MeetingsByStatus {
  status: string;
  count: number;
}

export interface MeetingsByDay {
  dayCode: string;
  count: number;
}

export interface TopExhibitorByMeetings {
  exhibitorId: number;
  exhibitorName: string;
  meetingCount: number;
}

export interface MeetingsMetrics {
  meetingsByStatus: MeetingsByStatus[];
  completionRate: number;
  noShowRate: number;
  meetingsByDay: MeetingsByDay[];
  topExhibitorsByMeetings: TopExhibitorByMeetings[];
}

// QR Badges Metrics
export interface ScansByType {
  type: string;
  count: number;
}

export interface MostScannedUser {
  userId: number;
  userName: string;
  scanCount: number;
}

export interface QrBadgesMetrics {
  totalQrScans: number;
  scansByType: ScansByType[];
  activeBadges: number;
  totalBadgeScans: number;
  mostScannedUsers: MostScannedUser[];
}

// Networking Metrics
export interface ConnectionsByStatus {
  status: string;
  count: number;
}

export interface NetworkingMetrics {
  connectionsByStatus: ConnectionsByStatus[];
  acceptanceRate: number;
  totalConversations: number;
  totalMessages: number;
  unreadMessages: number;
}

// Support Metrics
export interface QueriesByType {
  type: string;
  count: number;
}

export interface SupportMetrics {
  queriesByType: QueriesByType[];
  totalOpenQueries: number;
}

// Favorites Metrics
export interface FavoritesByType {
  type: string;
  count: number;
}

export interface TopFavoritedItem {
  id: number;
  name: string;
  favoriteCount: number;
}

export interface FavoritesMetrics {
  favoritesByType: FavoritesByType[];
  topFavoritedSessions: TopFavoritedItem[];
  topFavoritedExhibitors: TopFavoritedItem[];
  topFavoritedSpeakers: TopFavoritedItem[];
}

// Main Dashboard Metrics Response
export interface DashboardMetricsResponse {
  users: UserMetrics;
  exhibitors: ExhibitorMetrics;
  leads: LeadsMetrics;
  meetings: MeetingsMetrics;
  qrBadges: QrBadgesMetrics;
  networking: NetworkingMetrics;
  support: SupportMetrics;
  favorites: FavoritesMetrics;
}

// Chart color constants
export const CHART_COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
  pink: '#ec4899',
  lime: '#84cc16',
  palette: [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#ec4899',
    '#84cc16',
  ],
} as const;
